import { Agency, agencyBasic } from '@app/models';
import { OperationPoint, operationPoint, PcsEntity } from '@app/pcs/models';
import { EntityType, PropertyNameMapper } from '@app/shared/forms/property-mapper';
import { TrainComposition } from '@dossier/models/train-composition';
import {gql} from 'apollo-angular';

export interface CartechCapability {
  id: number;
  code: number;
  description: string;
}

export interface EmergencyBrakeOverride {
  id: number;
  description: string;
}

export interface ElectroPneumaticBrake {
  id: number;
  description: string;
}

export interface PassengerCompositionItem {
  id: number;
  trainCompositionId?: number;
  sortOrder?: number;
  agency?: Agency;
  impNo?: string;
  footnote?: string;
  carriageType?: string;
  sequenceNbr?: number;
  fromTrainNbr?: number;
  toTrainNbr?: number;
  ewpNo?: number;
  cartechCapability?: CartechCapability;
  adminAgency?: Agency;
  fromOperationPoint?: OperationPoint;
  toOperationPoint?: OperationPoint;
  previousRouteOp?: OperationPoint;
  subsequentRouteOp?: OperationPoint;
  emergencyBrakeOverride?: EmergencyBrakeOverride;
  electroPneumaticBrake?: ElectroPneumaticBrake;
}

export interface TrainCompositionFootnote {
  id: number;
  sortOrder?: number;
  trainCompositionId?: number;
  footnoteText?: string;
  footnote?: string;
}

export interface PassengerTrainComposition extends TrainComposition {
  compositionItems: PassengerCompositionItem[];
  footnotes: TrainCompositionFootnote[];
}

export const passengerTrainCompositionMapper = new PropertyNameMapper({
  trainHeadNote1: 'trainHeadNote1',
  trainHeadNote2: 'trainHeadNote2',
  freeText: 'freeText',
  compositionItems: new PropertyNameMapper({
    agency: 'agencyId',
    impNo: 'nationalCompNr',
    footnote: 'footNote',
    carriageType: 'waggonType',
    sequenceNbr: 'sequenceNbr',
    fromTrainNbr: 'fromTrainNbr',
    toTrainNbr: 'toTrainNbr',
    ewpNo: 'ewpNbr',
    cartechCapability: 'cartechCapabilityId',
    adminAgency: 'adminAgencyId',
    previousRouteOp: 'previousRoute',
    subsequentRouteOp: 'subsequentRoute',
    emergencyBrakeOverride: 'emergencyBrakeOverrideId',
    electroPneumaticBrake: 'electroPneumaticBrakeId'
  }, {entityName: PcsEntity.PassengerTrainCompositionItem, entityType: EntityType.Array}),
  footnotes: new PropertyNameMapper({
    footnoteText: 'footnoteText',
    footnote: 'footnote'
  }, {entityName: PcsEntity.TrainCompositionFootnote, entityType: EntityType.Array})
}, {entityName: PcsEntity.PassengerTrainComposition});

export const passengerCompositionItem = gql`
    fragment passengerCompositionItem on PassengerCompositionItem {
        id
        trainCompositionId
        sortOrder
        agency {
            ...agencyBasic
        }
        impNo
        footnote
        carriageType
        sequenceNbr
        fromTrainNbr
        toTrainNbr
        ewpNo
        cartechCapability {
            id
            code
            description
        }
        adminAgency {
            ...agencyBasic
        }
        fromOperationPoint {
            ...operationPoint
        }
        toOperationPoint {
            ...operationPoint
        }
        previousRouteOp {
            ...operationPoint
        }
        subsequentRouteOp {
            ...operationPoint
        }
        emergencyBrakeOverride {
            id
            description
        }
        electroPneumaticBrake {
            id
            description
        }
    }
    ${agencyBasic}
    ${operationPoint}
`;

const trainCompositionFootnote = gql`
    fragment trainCompositionFootnote on TrainCompositionFootnote {
        id
        sortOrder
        trainCompositionId
        footnoteText
        footnote
    }
`;

export const passengerTrainComposition = gql`
    fragment passengerTrainComposition on PassengerTrainComposition {
        passengerCompositionItems: compositionItems {
            ...passengerCompositionItem
        }
        footnotes {
            ...trainCompositionFootnote
        }
    }
    ${passengerCompositionItem}
    ${trainCompositionFootnote}
`;
